/**
 * Turns a pasted video link into one that is safe to put in an iframe.
 *
 * YouTube watch/share/shorts links send X-Frame-Options and refuse to load in
 * an iframe ("refused to connect"), so they have to be rewritten to /embed/.
 * Anything we don't recognise is returned untouched.
 */
export const toEmbedUrl = (url?: string | null): string => {
  const raw = url?.trim();
  if (!raw) return "";

  let parsed: URL;
  try {
    parsed = new URL(raw);
  } catch {
    return raw;
  }

  const host = parsed.hostname.replace(/^www\./, "").toLowerCase();

  if (host === "youtu.be") {
    const id = parsed.pathname.split("/").filter(Boolean)[0];
    return id ? withParams(`https://www.youtube.com/embed/${id}`, parsed) : raw;
  }

  if (host === "youtube.com" || host === "m.youtube.com") {
    if (parsed.pathname.startsWith("/embed/")) return raw;

    const [first, second] = parsed.pathname.split("/").filter(Boolean);

    // /watch?v=ID
    const watchId = parsed.searchParams.get("v");
    if (watchId) {
      parsed.searchParams.delete("v");
      return withParams(`https://www.youtube.com/embed/${watchId}`, parsed);
    }

    // /shorts/ID and /live/ID
    if ((first === "shorts" || first === "live") && second) {
      return withParams(`https://www.youtube.com/embed/${second}`, parsed);
    }

    return raw;
  }

  if (host === "youtube-nocookie.com") return raw;

  // vimeo.com/123456789 -> player.vimeo.com/video/123456789
  if (host === "vimeo.com") {
    const id = parsed.pathname.split("/").filter(Boolean)[0];
    if (id && /^\d+$/.test(id)) {
      return withParams(`https://player.vimeo.com/video/${id}`, parsed);
    }
  }

  return raw;
};

const withParams = (base: string, source: URL): string => {
  const params = new URLSearchParams(source.searchParams);
  // Share links carry tracking junk the player doesn't need.
  ["si", "feature", "pp", "ab_channel"].forEach((key) => params.delete(key));

  // ?t=90 / ?t=1m30s on a watch link is ?start=90 on an embed.
  const t = params.get("t");
  if (t) {
    params.delete("t");
    const seconds = parseTimeToSeconds(t);
    if (seconds) params.set("start", String(seconds));
  }

  const query = params.toString();
  return query ? `${base}?${query}` : base;
};

const parseTimeToSeconds = (value: string): number => {
  if (/^\d+$/.test(value)) return Number(value);
  const match = value.match(/^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/i);
  if (!match) return 0;
  const [, h, m, s] = match;
  return Number(h || 0) * 3600 + Number(m || 0) * 60 + Number(s || 0);
};
